import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export type VendorStatus = 'LONG_LIST' | 'SHORT_LIST' | 'DEMONSTRATED' | 'SELECTED' | 'DECLINED';
export type CostKind = 'ONE_TIME' | 'ANNUAL' | 'MONTHLY' | 'PER_LOAN_MONTH';

export const VENDOR_STATUS: Record<VendorStatus, { label: string; tone: 'neutral' | 'info' | 'warn' | 'ok' | 'bad' }> =
  {
    LONG_LIST: { label: 'Long list', tone: 'neutral' },
    SHORT_LIST: { label: 'Short list', tone: 'info' },
    DEMONSTRATED: { label: 'Demonstrated', tone: 'warn' },
    SELECTED: { label: 'Selected', tone: 'ok' },
    DECLINED: { label: 'Declined', tone: 'bad' },
  };
export const COST_KIND: Record<CostKind, string> = {
  ONE_TIME: 'One-time',
  ANNUAL: 'Per year',
  MONTHLY: 'Per month',
  PER_LOAN_MONTH: 'Per loan per month',
};

export interface CategoryRow {
  id: string;
  name: string;
  vendors: number;
  criteria: number;
  evaluators: number;
  submitted: number;
  isEvaluator: boolean;
  iSubmitted: boolean;
}

export interface Cost {
  id?: string;
  label: string;
  kind: CostKind;
  amount: string;
  annualEscalationPct: string;
  note: string | null;
}

export interface Vendor {
  id: string;
  name: string;
  status: VendorStatus;
  contact: string | null;
  notes: string | null;
  website: string | null;
  documents: Array<{ id: string; title: string }>;
  costs: Cost[];
}

export interface CategoryDetail {
  id: string;
  name: string;
  canManage: boolean;
  isEvaluator: boolean;
  iSubmitted: boolean;
  seesResults: boolean;
  criteria: Array<{ id: string; name: string; weight: number }>;
  vendors: Vendor[];
  evaluators: Array<{ userId: string; name: string; submitted: boolean }>;
  myScores: Array<{
    vendorId: string;
    criterionId: string;
    score: number;
    notes: string | null;
    aiRunId: string | null;
  }>;
}

export interface MatrixRow {
  vendorId: string;
  name: string;
  status: VendorStatus;
  perCriterion: Array<{ criterionId: string; mean: number | null; spread: number | null; n: number }>;
  weightedTotal: number | null;
  complete: boolean;
  rank: number | null;
  tco: { total: string; byYear: string[]; incomplete: boolean } | null;
}
export interface Matrix {
  criteria: Array<{ id: string; name: string; weight: number }>;
  weights: Record<string, number>;
  totalWeight: number;
  evaluatorsSubmitted: number;
  years: number;
  loanCount: number | null;
  rows: MatrixRow[];
}

export const vendorsKey = (programId: string) => ['vendors', programId] as const;

export const useCategories = (programId: string) =>
  useQuery({
    queryKey: [...vendorsKey(programId), 'categories'],
    queryFn: () => api.get<CategoryRow[]>(`/api/programs/${programId}/vendors/categories`),
  });

export const useCategory = (programId: string, id: string) =>
  useQuery({
    queryKey: [...vendorsKey(programId), 'category', id],
    queryFn: () => api.get<CategoryDetail>(`/api/programs/${programId}/vendors/categories/${id}`),
  });

export const money = (s: string | number) =>
  Number(s).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
